import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { AppService } from './core/services/app.service';
import { EmbeddedScrollBridgeService } from './core/services/embedded-scroll-bridge.service';
import { LoggingService } from './core/services/logging.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, AfterViewChecked {
  title = 'jiox-admin';
  showContent = false;
  isPreview = false;
  // True while the user is on /login or /login/admin — bypasses the
  // shell so the bare router-outlet renders even when unauthenticated.
  isLoginPage = false;
  showSideNavContent = true;
  isEmbeddedView = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LoggingService);
  private readonly renderer = inject(Renderer2);
  private readonly embeddedScrollBridge = inject(EmbeddedScrollBridgeService);
  private scrollBridgeAttached = false;

  @ViewChild('embeddedViews')
  private embeddedViews?: ElementRef<HTMLElement>;

  constructor(
    private readonly router: Router,
    private readonly keycloakService: KeycloakService,
    private readonly appService: AppService,
    private readonly route: ActivatedRoute
  ) {
    const queryParams = new URLSearchParams(window.location.search);
    const isEmbedded = queryParams.get('isEmbedded');
    if (isEmbedded === 'true') {
      this.appService.setIsEmbeddedView(true);
      this.isEmbeddedView = true;
      this.syncEmbeddedBodyClass(true);
    }

    this.appService
      .isPreviewPage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((d) => {
        if (d !== undefined && d !== null) {
          this.isPreview = d;
        }
      });

    // Initial check based on current URL, then update on every nav.
    this.isLoginPage = this.router.url.startsWith('/login');
    this.isPreview = this.router.url.startsWith('/preview');
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        this.isLoginPage = e.urlAfterRedirects.startsWith('/login');
        this.isPreview = e.urlAfterRedirects.startsWith('/preview');

        // After any navigation, re-evaluate auth state. The queryParams
        // subscription below only fires on initial load (SSO comes back
        // via full page reload), so for the direct-grant /login/admin
        // → /dashboard transition we'd otherwise never flip showContent
        // to true — leaving the dashboard route blank.
        if (!this.isLoginPage && this.keycloakService.isLoggedIn()) {
          this.appService.checkRoles();
          this.showContent = true;
        }
      });
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const isEmbedded = String(params['isEmbedded'] ?? '');
        if (isEmbedded === 'true') {
          this.appService.setIsEmbeddedView(true);
          this.isEmbeddedView = true;
          this.syncEmbeddedBodyClass(true);
        } else if (isEmbedded === 'false') {
          this.appService.setIsEmbeddedView(false);
          this.isEmbeddedView = false;
          this.syncEmbeddedBodyClass(false);
        }

        const isAuthenticated = this.keycloakService.isLoggedIn();
        if (isAuthenticated) {
          this.appService.checkRoles();
          this.appService.loadTenantLogo();
          this.showContent = true;
        } else if (isEmbedded === 'true') {
          this.showContent = true;
        }
      });
  }

  ngAfterViewChecked(): void {
    if (!this.isEmbeddedView || !this.showContent || this.isPreview) {
      if (this.scrollBridgeAttached) {
        this.embeddedScrollBridge.detach();
        this.scrollBridgeAttached = false;
      }
      return;
    }

    const scroller = this.embeddedViews?.nativeElement;

    if (scroller && !this.scrollBridgeAttached) {
      this.embeddedScrollBridge.attach(scroller);
      this.scrollBridgeAttached = true;
    }
  }

  redirect(): void {
    this.router
      .navigate(['home'])
      .catch((e: unknown) => this.logger.error('Navigation failed', e));
  }

  getSidebarWidth(isEmit: boolean): void {
    if (isEmit) {
      this.showSideNavContent = !this.showSideNavContent;
    }
  }

  /** Lock document scroll in iframe; only .views--embedded should scroll. */
  private syncEmbeddedBodyClass(isEmbedded: boolean): void {
    if (isEmbedded) {
      this.renderer.addClass(document.body, 'embedded-view');
      return;
    }
    this.renderer.removeClass(document.body, 'embedded-view');
    this.embeddedScrollBridge.detach();
    this.scrollBridgeAttached = false;
  }
}
